/** Earliest AU financial year start year allowed (2008-2009). */
export const FINANCIAL_YEAR_MIN_START = 2008;

export const FINANCIAL_YEAR_CUSTOM_SELECT_VALUE = "__custom__";

export type FinancialYearOption = {
  value: string;
  label: string;
  startYear: number;
  endYear: number;
};

/**
 * AU FY ends in June. From July onward the active FY end year is calendar year + 1.
 * e.g. June 2026 → 2025-2026; July 2026 → 2026-2027.
 */
export function getAustralianFinancialYearEnd(
  referenceDate: Date = new Date(),
): number {
  const month = referenceDate.getMonth();
  const year = referenceDate.getFullYear();
  return month >= 6 ? year + 1 : year;
}

export function formatFinancialYear(
  startYear: number,
  endYear: number,
): string {
  return `${startYear}-${endYear}`;
}

export function parseFinancialYear(
  value: string,
): { start: number; end: number } | null {
  const match = /^(\d{4})-(\d{4})$/.exec(String(value ?? "").trim());
  if (!match) return null;
  const start = Number(match[1]);
  const end = Number(match[2]);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { start, end };
}

/**
 * Build every AU financial year from `minStartYear` through `maxEndYear` (inclusive).
 * Default max is current FY end + 1 (next FY).
 */
export function buildAustralianFinancialYears(
  minStartYear: number = FINANCIAL_YEAR_MIN_START,
  maxEndYear?: number,
  referenceDate: Date = new Date(),
): FinancialYearOption[] {
  const endCap = maxEndYear ?? getAustralianFinancialYearEnd(referenceDate) + 1;
  const options: FinancialYearOption[] = [];

  for (let end = endCap; end > minStartYear; end--) {
    const start = end - 1;
    if (start < minStartYear) break;
    options.push({
      value: formatFinancialYear(start, end),
      label: formatFinancialYear(start, end),
      startYear: start,
      endYear: end,
    });
  }

  return options;
}

/** Recent FY options for compact dropdowns (newest first). Includes next FY on top. */
export function getRecentAustralianFinancialYears(
  recentCount = 6,
  referenceDate: Date = new Date(),
): FinancialYearOption[] {
  const currentEnd = getAustralianFinancialYearEnd(referenceDate);
  const options: FinancialYearOption[] = [];
  const topEnd = currentEnd + 1;

  for (let i = 0; i <= recentCount; i++) {
    const end = topEnd - i;
    const start = end - 1;
    if (start < FINANCIAL_YEAR_MIN_START) break;
    options.push({
      value: formatFinancialYear(start, end),
      label: formatFinancialYear(start, end),
      startYear: start,
      endYear: end,
    });
  }

  return options;
}

export function getMaxAllowedFinancialYearEnd(
  referenceDate: Date = new Date(),
): number {
  return getAustralianFinancialYearEnd(referenceDate) + 1;
}

export function isValidCustomFinancialYear(
  startYear: number,
  endYear: number,
  referenceDate: Date = new Date(),
): boolean {
  if (!Number.isInteger(startYear) || !Number.isInteger(endYear)) return false;
  if (endYear !== startYear + 1) return false;
  if (startYear < FINANCIAL_YEAR_MIN_START) return false;
  if (endYear > getMaxAllowedFinancialYearEnd(referenceDate)) return false;
  return true;
}
